import ErrorIcon from '@mui/icons-material/Error';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { SubmitHandler } from 'react-hook-form/dist/types';
import { useAppDispatch, useAppSelector } from '../../helpers/hooks';
import { FormKyc } from '../../types';
import { appActions } from './app-slice';

function KYC() {
  const { isLoading, dbEnterprise } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();

  const defaultValues: FormKyc = {
    businessDescription: dbEnterprise?.kycData?.businessDescription ?? '',
    promotionChannels: dbEnterprise?.kycData?.promotionChannels ?? '',
    serviceCoverage: dbEnterprise?.kycData?.serviceCoverage ?? '',
  };

  const { register, handleSubmit, formState, reset } = useForm<FormKyc>({
    defaultValues,
  });

  const { errors, isDirty } = formState;

  useEffect(() => {
    dispatch(appActions.setIsCurrentFormDirty(isDirty));
  }, [isDirty]);

  const onSubmit: SubmitHandler<FormKyc> = (data) => {
    dispatch(appActions.saveKycData(data));
    dispatch(appActions.setIsCurrentFormDirty(false));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack direction="column" gap={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="column" gap={2}>
              <Typography variant="body1">
                Descreva em algumas palavras o que você vende ou qual serviço
                oferece.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrição do negócio"
              type="text"
              placeholder="Ex: doces por encomenda, aulas particulares, manicure em domicílio..."
              multiline
              maxRows={4}
              rows={4}
              {...register('businessDescription', {
                required: 'O campo é obrigatório',
                minLength: {
                  value: 30,
                  message: 'A descrição é muito curta',
                },
              })}
              error={!!errors.businessDescription}
              helperText={errors.businessDescription?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="column" gap={2}>
              <Typography variant="body1">
                Informe qual é o site ou rede social (Facebook, Instagram) onde
                você divulga o seu serviço.
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="@anadoces no Instagram, www.anadoces.com.br"
              type="text"
              placeholder="Ex: @anadoces no Instagram, www.anadoces.com.br"
              {...register('promotionChannels', {
                required: 'O campo é obrigatório',
                minLength: {
                  value: 10,
                  message: 'O campo é muito curto',
                },
              })}
              error={!!errors.promotionChannels}
              helperText={errors.promotionChannels?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl>
              <FormLabel id="serviceCoverage">
                Você atende apenas na sua cidade ou em outras também?
              </FormLabel>
              <RadioGroup
                aria-labelledby="serviceCoverage"
                defaultValue={defaultValues.serviceCoverage}
              >
                <FormControlLabel
                  value="local"
                  control={
                    <Radio
                      {...register('serviceCoverage', {
                        required: 'Obrigatório',
                      })}
                    />
                  }
                  label="Só na minha cidade"
                />
                <FormControlLabel
                  value="regional"
                  control={
                    <Radio
                      {...register('serviceCoverage', {
                        required: 'Obrigatório',
                      })}
                    />
                  }
                  label="Em cidades vizinhas"
                />
                <FormControlLabel
                  value="national"
                  control={
                    <Radio
                      {...register('serviceCoverage', {
                        required: 'O campo é Obrigatório',
                      })}
                    />
                  }
                  label="Em todo o Brasil"
                />
              </RadioGroup>
              <FormHelperText error={!!errors.serviceCoverage}>
                {errors.serviceCoverage?.message}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        <Stack direction="row" gap={2} width={1}>
          {isDirty && dbEnterprise && (
            <Stack flexBasis="100%">
              <LoadingButton
                loading={isLoading}
                onClick={() => reset()}
                variant="outlined"
                color="error"
                disabled={!isDirty}
              >
                Descartar
              </LoadingButton>
            </Stack>
          )}
          <Stack flexBasis="100%">
            <LoadingButton
              loading={isLoading}
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              disabled={!isDirty}
              type="submit"
            >
              {dbEnterprise ? 'Salvar' : 'Próximo'}
            </LoadingButton>
          </Stack>
        </Stack>
        {Object.keys(errors).length > 0 && (
          <Stack direction="row" alignItems="center" justifyContent="center">
            <ErrorIcon color="error" />
            <Box paddingLeft={1}>
              <Typography variant="caption" textAlign="center" color="error">
                Por favor corrija os erros antes de prosseguir
              </Typography>
            </Box>
          </Stack>
        )}
      </Stack>
    </form>
  );
}

export default KYC;
